/**
 * Per-browser learned allowlist for the spam-protection layer.
 *
 * Stores tokens harvested from confidently-passing form submissions so that
 * future submissions containing the same trade-specific words bypass the
 * keyword blocklist (honeypot + time-trap STILL apply).
 *
 * Safety guardrails (conservative profile):
 *   - Only call `learnFromSubmission` when the result is OK *and* eligibility
 *     conditions are met (whitelist matched, elapsed > 8s, message ≥ 40 chars).
 *   - Tokens must be 4–20 chars, lowercase a–z only, not in STOPWORDS, and
 *     must NOT match any SPAM_PATTERN.
 *   - Each token has an independent 24h TTL.
 *   - Capped at MAX_TOKENS; oldest evicted first.
 *   - localStorage only — never sent to a server, never shared across users.
 */
import { SPAM_PATTERNS } from "./useSpamProtection";

const STORAGE_KEY = "hes_spam_allowlist_v1";
const MAX_TOKENS = 100;
const TTL_MS = 24 * 60 * 60 * 1000; // 24h
const MIN_TOKEN_LEN = 4;
const MAX_TOKEN_LEN = 20;

// Eligibility thresholds for harvesting tokens from a successful submission.
export const LEARN_MIN_ELAPSED_MS = 8000;
export const LEARN_MIN_MESSAGE_CHARS = 40;

// Common English / form-filler words that carry no signal.
const STOPWORDS = new Set([
  "about", "above", "after", "again", "also", "always", "another", "anyone",
  "anything", "around", "because", "been", "before", "being", "below",
  "between", "both", "came", "come", "could", "didn", "does", "doing", "done",
  "down", "during", "each", "even", "ever", "every", "from", "give", "going",
  "gone", "good", "great", "have", "having", "hello", "help", "here", "into",
  "just", "know", "knows", "last", "less", "like", "likely", "look", "made",
  "make", "many", "more", "most", "much", "must", "name", "need", "needs",
  "next", "nice", "only", "other", "others", "ours", "over", "people",
  "place", "please", "really", "right", "said", "same", "send", "should",
  "since", "some", "soon", "still", "such", "sure", "take", "tell", "thank",
  "thanks", "that", "their", "them", "then", "there", "these", "they",
  "thing", "things", "this", "those", "through", "today", "together", "took",
  "under", "until", "upon", "used", "uses", "using", "very", "want", "wanted",
  "wants", "well", "were", "what", "when", "where", "which", "while", "will",
  "with", "without", "work", "would", "your", "yours", "yourself",
]);

type StoredToken = { token: string; addedAt: number };

function safeRead(): StoredToken[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is StoredToken =>
        !!t && typeof t === "object" && typeof (t as StoredToken).token === "string" && typeof (t as StoredToken).addedAt === "number"
    );
  } catch {
    return [];
  }
}

function safeWrite(tokens: StoredToken[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // localStorage may be disabled (private mode, quota). Fail silently.
  }
}

function pruneExpired(tokens: StoredToken[], now: number): StoredToken[] {
  return tokens.filter((t) => now - t.addedAt < TTL_MS);
}

/** Returns the current valid set of learned tokens. */
export function getLearnedAllowlist(): Set<string> {
  const now = Date.now();
  const fresh = pruneExpired(safeRead(), now);
  // Persist pruning so storage doesn't grow unbounded across visits.
  safeWrite(fresh);
  return new Set(fresh.map((t) => t.token));
}

/** Test whether any token in `text` is on the learned allowlist. */
export function textHasLearnedToken(text: string): boolean {
  if (!text) return false;
  const set = getLearnedAllowlist();
  if (set.size === 0) return false;
  for (const word of extractCandidateTokens(text)) {
    if (set.has(word)) return true;
  }
  return false;
}

/** Lowercase, alpha-only words within the allowed length range. */
function extractCandidateTokens(text: string): string[] {
  const out: string[] = [];
  const matches = text.toLowerCase().match(/[a-z]{4,20}/g);
  if (!matches) return out;
  for (const w of matches) {
    if (w.length < MIN_TOKEN_LEN || w.length > MAX_TOKEN_LEN) continue;
    if (STOPWORDS.has(w)) continue;
    out.push(w);
  }
  return out;
}

/** True if any spam pattern matches the bare token — never learn these. */
function tokenMatchesAnySpamRule(token: string): boolean {
  return SPAM_PATTERNS.some((p) => p.test(token));
}

export type LearnContext = {
  /** ms elapsed between mount and submit. */
  elapsedMs: number;
  /** Whether the submission already matched a static WHITELIST_PATTERN. */
  whitelisted: boolean;
  /** The raw message body (longest free-text field). */
  message: string;
};

/**
 * Harvest tokens from a confidently-passing submission. No-op if eligibility
 * checks fail. Returns the tokens actually added (for debugging).
 */
export function learnFromSubmission(ctx: LearnContext): string[] {
  // Hard eligibility — defense against learning from borderline submissions.
  if (!ctx.whitelisted) return [];
  if (ctx.elapsedMs < LEARN_MIN_ELAPSED_MS) return [];
  if (!ctx.message || ctx.message.length < LEARN_MIN_MESSAGE_CHARS) return [];

  const candidates = extractCandidateTokens(ctx.message)
    .filter((t) => !tokenMatchesAnySpamRule(t));

  if (candidates.length === 0) return [];

  const now = Date.now();
  const existing = pruneExpired(safeRead(), now);
  const existingMap = new Map(existing.map((t) => [t.token, t]));

  const added: string[] = [];
  for (const token of candidates) {
    if (!existingMap.has(token)) {
      existingMap.set(token, { token, addedAt: now });
      added.push(token);
    } else {
      // Refresh TTL on re-encounter so active vocab stays fresh.
      existingMap.set(token, { token, addedAt: now });
    }
  }

  // Cap: keep the most-recently-added MAX_TOKENS entries.
  let merged = Array.from(existingMap.values()).sort((a, b) => b.addedAt - a.addedAt);
  if (merged.length > MAX_TOKENS) merged = merged.slice(0, MAX_TOKENS);

  safeWrite(merged);
  return added;
}

/** Test-only / debug helper: clear the learned allowlist. */
export function clearLearnedAllowlist(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
